import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Pause, Play, SkipForward, Plus } from 'lucide-react';
import { playTick, playRecoveryEndCue } from '@/lib/workoutPrefs';

function fmtTime(s) {
  const m = Math.floor(Math.max(0, s) / 60);
  const sec = Math.max(0, s) % 60;
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

/**
 * Reusable recovery countdown used by EVERY workout type (HIIT circuits AND strength sets/exercises).
 * Starts at the exact programmed `seconds`. Beeps + vibrates for the final 5 seconds, plays a distinct
 * completion cue at zero, then calls onDone. Pause / Skip / +15 / +30 affect only this live countdown —
 * they never mutate the workout template. Skip advances immediately without the completion cue.
 */
export function RecoveryTimer({ seconds, title = 'RECOVERY', badge, nextLabel, onDone }) {
  const [left, setLeft] = useState(seconds);
  const [paused, setPaused] = useState(false);
  const fired = useRef(false);

  useEffect(() => { setLeft(seconds); setPaused(false); fired.current = false; }, [seconds]);

  // Countdown driver.
  useEffect(() => {
    if (paused || fired.current) return;
    if (left <= 0) {
      fired.current = true;
      playRecoveryEndCue();
      onDone?.();
      return;
    }
    const t = setInterval(() => setLeft(s => s - 1), 1000);
    return () => clearInterval(t);
  }, [paused, left]);

  // Audible 3-2-1-style countdown across the FINAL 5 seconds.
  useEffect(() => {
    if (paused) return;
    if (left >= 1 && left <= 5) playTick();
  }, [paused, left]);

  const skip = () => { fired.current = true; onDone?.(); };

  return (
    <div className="text-center" data-testid="recovery-timer">
      {badge && (
        <div className="mb-2">
          <div className="pill bg-[var(--c-green-soft)] text-[var(--c-green-ink)] inline-flex">{badge}</div>
        </div>
      )}
      <div className="label-mini text-[#00C8FF]">{title}</div>
      <div className={`font-display font-black tabular-nums mt-1 ${paused ? 'text-muted-foreground' : (left <= 5 ? 'text-[#FFB800]' : 'text-[var(--c-ink)]')}`} style={{ fontSize: '4rem', lineHeight: 1 }} data-testid="recovery-timer-value">
        {fmtTime(left)}
      </div>
      {paused && <div className="text-xs text-[#FFB800] mt-1">Paused</div>}
      {nextLabel && <div className="text-xs text-muted-foreground mt-2" data-testid="recovery-next">NEXT: {nextLabel}</div>}

      <div className="grid grid-cols-2 gap-2 mt-5">
        <Button onClick={() => setPaused(p => !p)} data-testid="recovery-pause" variant="outline" className="border-[var(--c-hair)] rounded-xl">
          {paused ? <><Play className="w-4 h-4 mr-1.5" /> Resume</> : <><Pause className="w-4 h-4 mr-1.5" /> Pause</>}
        </Button>
        <Button onClick={skip} data-testid="recovery-skip" variant="outline" className="border-[var(--c-hair)] rounded-xl">
          <SkipForward className="w-4 h-4 mr-1.5" /> Skip
        </Button>
      </div>
      <div className="grid grid-cols-2 gap-2 mt-2">
        <Button onClick={() => setLeft(s => s + 15)} data-testid="recovery-add-15" variant="ghost" className="rounded-xl text-[#00C8FF]"><Plus className="w-4 h-4 mr-1" /> 15s</Button>
        <Button onClick={() => setLeft(s => s + 30)} data-testid="recovery-add-30" variant="ghost" className="rounded-xl text-[#00C8FF]"><Plus className="w-4 h-4 mr-1" /> 30s</Button>
      </div>
    </div>
  );
}
